import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, Star, AlertCircle, Loader2, MessageCircle } from 'lucide-react';
import { useUpgradeData, useCreateCheckout } from '../hooks/useUpgrade';
import type { UpgradePlan } from '../services/upgradeService';

const WHATSAPP_NUMBER = '447934514016';

const formatPrice = (price: string, currency: string) => {
  const num = parseFloat(price);
  if (currency === 'BRL') {
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  }
  return num.toLocaleString('en-GB', { minimumFractionDigits: 2 });
};

const getCurrencySymbol = (currency: string) => {
  switch (currency) {
    case 'BRL': return 'R$';
    case 'GBP': return '£';
    case 'USD': return '$';
    default: return currency;
  }
};

// Translations
const t = {
  'pt-BR': {
    greeting: (name: string) => `Olá, ${name}!`,
    trialEnded: 'Seu período de teste acabou.',
    choosePlan: 'Escolha o plano ideal para você:',
    recommended: 'Recomendado',
    monthly: '/mês',
    yearly: '/ano',
    subscribe: 'Assinar',
    subscribing: 'Redirecionando...',
    annualSavings: 'Pague anual e economize 2 meses!',
    annualBilling: 'Pagamento anual',
    monthlyBilling: 'Pagamento mensal',
    loading: 'Carregando...',
    invalidToken: 'Link inválido ou expirado',
    invalidTokenDesc: 'Este link de upgrade não é mais válido. Envie uma mensagem no WhatsApp para receber um novo link.',
    requestNewLink: 'Pedir novo link',
    error: 'Ocorreu um erro',
    tryAgain: 'Tentar novamente',
    help: 'Precisa de ajuda?',
    helpDesc: 'Fale com a gente pelo WhatsApp.',
  },
  'en-GB': {
    greeting: (name: string) => `Hello, ${name}!`,
    trialEnded: 'Your trial period has ended.',
    choosePlan: 'Choose the best plan for you:',
    recommended: 'Recommended',
    monthly: '/month',
    yearly: '/year',
    subscribe: 'Subscribe',
    subscribing: 'Redirecting...',
    annualSavings: 'Pay yearly and save 2 months!',
    annualBilling: 'Annual billing',
    monthlyBilling: 'Monthly billing',
    loading: 'Loading...',
    invalidToken: 'Invalid or expired link',
    invalidTokenDesc: 'This upgrade link is no longer valid. Send us a message on WhatsApp to get a new link.',
    requestNewLink: 'Request new link',
    error: 'An error occurred',
    tryAgain: 'Try again',
    help: 'Need help?',
    helpDesc: 'Chat with us on WhatsApp.',
  },
};

function PlanCard({
  plan,
  isPopular,
  billingCycle,
  currency,
  lang,
  onSubscribe,
  isLoading,
  loadingPlan,
}: {
  plan: UpgradePlan;
  isPopular: boolean;
  billingCycle: 'monthly' | 'yearly';
  currency: string;
  lang: 'pt-BR' | 'en-GB';
  onSubscribe: (slug: string) => void;
  isLoading: boolean;
  loadingPlan: string | null;
}) {
  const texts = t[lang];
  const currencySymbol = getCurrencySymbol(currency);
  const price = billingCycle === 'yearly' && plan.priceYearly
    ? plan.priceYearly
    : plan.priceMonthly;
  const period = billingCycle === 'yearly' ? texts.yearly : texts.monthly;
  const isThisLoading = isLoading && loadingPlan === plan.slug;

  return (
    <div
      className={`relative rounded-2xl p-6 sm:p-8 shadow-lg transition-transform hover:scale-[1.02] ${
        isPopular
          ? 'bg-green-500 text-white ring-4 ring-green-300 dark:ring-green-700'
          : 'bg-white dark:bg-gray-800'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 text-sm font-bold px-4 py-1 rounded-full flex items-center gap-1">
          <Star className="w-4 h-4" />
          {texts.recommended}
        </div>
      )}

      <h3 className={`text-2xl font-bold mb-1 ${isPopular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
        {plan.name}
      </h3>

      {plan.description && (
        <p className={`text-sm mb-4 ${isPopular ? 'text-green-100' : 'text-gray-500 dark:text-gray-400'}`}>
          {plan.description}
        </p>
      )}

      <div className="flex items-baseline gap-1 mb-6">
        <span className={`text-4xl font-bold ${isPopular ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
          {currencySymbol}{formatPrice(price, currency)}
        </span>
        <span className={isPopular ? 'text-green-100' : 'text-gray-500 dark:text-gray-400'}>
          {period}
        </span>
      </div>

      <ul className="space-y-3 mb-8">
        {plan.features.map((feature, i) => (
          <li
            key={i}
            className={`flex items-start gap-2 ${
              isPopular ? 'text-white' : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isPopular ? 'text-white' : 'text-green-500'}`} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={() => onSubscribe(plan.slug)}
        disabled={isLoading}
        className={`w-full py-3 rounded-full font-semibold transition flex items-center justify-center gap-2 ${
          isPopular
            ? 'bg-white text-green-600 hover:bg-green-50 disabled:bg-green-50'
            : 'bg-green-500 text-white hover:bg-green-600 disabled:bg-green-300'
        }`}
      >
        {isThisLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {texts.subscribing}
          </>
        ) : (
          texts.subscribe
        )}
      </button>
    </div>
  );
}

function InvalidTokenPage({ lang }: { lang: 'pt-BR' | 'en-GB' }) {
  const texts = t[lang];
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    lang === 'pt-BR' ? 'Oi! Preciso de um novo link de upgrade' : 'Hi! I need a new upgrade link'
  )}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full mb-6">
          <AlertCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          {texts.invalidToken}
        </h1>

        <p className="text-gray-600 dark:text-gray-300 mb-8">
          {texts.invalidTokenDesc}
        </p>

        <a
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-green-500 text-white font-semibold px-6 py-3 rounded-full hover:bg-green-600 transition"
        >
          <MessageCircle className="w-5 h-5" />
          {texts.requestNewLink}
        </a>

        <div className="mt-8">
          <Link to="/" className="text-gray-500 dark:text-gray-400 hover:underline">
            ← {lang === 'pt-BR' ? 'Voltar ao início' : 'Back to home'}
          </Link>
        </div>
      </div>
    </div>
  );
}

function LoadingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-green-500 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-300">Loading...</p>
      </div>
    </div>
  );
}

export default function UpgradePage() {
  const { token } = useParams<{ token: string }>();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useUpgradeData(token || '');
  const checkoutMutation = useCreateCheckout(token || '');

  // Determine language (default to pt-BR)
  const lang = data?.user?.language || 'pt-BR';
  const texts = t[lang];

  const handleSubscribe = (planSlug: string) => {
    setLoadingPlan(planSlug);
    checkoutMutation.mutate(
      { planSlug, billingCycle },
      {
        onError: () => setLoadingPlan(null),
      }
    );
  };

  // Loading state
  if (isLoading) {
    return <LoadingPage />;
  }

  // Error state (invalid token)
  if (isError || !data) {
    const errorLang = (error as any)?.response?.data?.message?.includes('pt-BR') ? 'pt-BR' : 'en-GB';
    return <InvalidTokenPage lang={errorLang} />;
  }

  const { user, plans, currency } = data;
  const sortedPlans = [...plans].sort((a, b) => a.displayOrder - b.displayOrder);

  // Calculate annual savings
  const hasYearlyPricing = plans.some(p => p.priceYearly);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Nav */}
      <nav className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="KeepNota" className="h-8 w-auto" />
            <span className="font-bold text-xl text-gray-900 dark:text-white">KeepNota</span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            {texts.greeting(user.name || user.phoneNumber)}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-2">
            {texts.trialEnded}
          </p>
          <p className="text-gray-500 dark:text-gray-400">
            {texts.choosePlan}
          </p>
        </div>

        {/* Billing Toggle */}
        {hasYearlyPricing && (
          <div className="flex flex-col items-center mb-8">
            <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-full p-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  billingCycle === 'monthly'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {texts.monthlyBilling}
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                  billingCycle === 'yearly'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
                    : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {texts.annualBilling}
              </button>
            </div>
            {billingCycle === 'yearly' && (
              <p className="mt-2 text-sm text-green-600 dark:text-green-400 font-medium">
                💡 {texts.annualSavings}
              </p>
            )}
          </div>
        )}

        {/* Plans */}
        <div className={`grid gap-6 sm:gap-8 ${sortedPlans.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
          {sortedPlans.map((plan, index) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isPopular={plan.type === 'pro' || index === sortedPlans.length - 1}
              billingCycle={billingCycle}
              currency={currency}
              lang={lang}
              onSubscribe={handleSubscribe}
              isLoading={checkoutMutation.isPending}
              loadingPlan={loadingPlan}
            />
          ))}
        </div>

        {/* Error Message */}
        {checkoutMutation.isError && (
          <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-center">
            <p className="text-red-600 dark:text-red-400">
              {texts.error}. {texts.tryAgain}.
            </p>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 dark:text-gray-400 mb-2">{texts.help}</p>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-green-600 dark:text-green-400 font-medium hover:underline"
          >
            <MessageCircle className="w-5 h-5" />
            {texts.helpDesc}
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 text-center text-gray-500 dark:text-gray-400 text-sm">
        <p>© 2025 KeepNota by Fig & Rosa Ltd.</p>
      </footer>
    </div>
  );
}
